import { useCallback, useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Panel } from "./ui";

/** TikTok Events API: status, test code and a test-event button. The token itself is a server secret. */
export const TikTokEventsPanel = ({ settings, onSave, TextField }) => {
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState({
    tiktok_test_event_code: settings.tiktok_test_event_code || "",
    tiktok_events_api_enabled: settings.tiktok_events_api_enabled || "on",
  });
  const load = useCallback(() => {
    api.get("/admin/settings/tiktok-events").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => { await onSave(draft); load(); };
  const test = async () => {
    setMsg("Sending…");
    try {
      if (draft.tiktok_test_event_code !== (settings.tiktok_test_event_code || "")) await onSave(draft);
      const { data } = await api.post("/admin/settings/tiktok-events/test");
      setMsg(data.ok ? "Test event accepted by TikTok — check Events Manager → Test events."
                     : data.reason === "not_configured" ? "The Events API access token isn't set yet."
                     : `TikTok refused it: ${data.message || data.reason || data.status}`);
      load();
    } catch (e) { setMsg(errText(e)); }
  };
  const last = status?.last_event;

  return (
    <Panel title="TikTok Events API (server-side)" testid="tiktok-events-panel">
      <p className="text-xs text-muted">
        Purchases, checkouts and add-to-cart are also sent from the server, so ad blockers can't hide them.
        TikTok keeps one copy of each event (same event id as the pixel).
      </p>
      <p className="text-xs" data-testid="tiktok-events-status">
        Status: <b className={status?.ready ? "text-ink" : "text-danger"}>
          {status?.ready ? `connected — pixel ${status.pixel_id}` : status?.token_configured ? "add the TikTok pixel ID above" : "access token not set"}
        </b>
        {last ? <span className="block text-muted mt-1" data-testid="tiktok-events-last">
          Last event {last.event} · {last.at?.slice(0, 16).replace("T", " ")} · {last.ok ? "accepted" : "refused"}{last.test ? " (test)" : ""}
        </span> : null}
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        <TextField label="Test event code" placeholder="TEST12345" testid="tiktok-test-event-code"
                   hint="From TikTok Events Manager → Test events. Clear it when you're done testing, or events won't count."
                   value={draft.tiktok_test_event_code} onChange={(v) => setDraft({ ...draft, tiktok_test_event_code: v })} />
        <label className="block text-xs">
          <span className="text-ink font-medium">Server events</span>
          <select className="field mt-1" data-testid="tiktok-events-enabled" value={draft.tiktok_events_api_enabled}
                  onChange={(e) => setDraft({ ...draft, tiktok_events_api_enabled: e.target.value })}>
            <option value="on">On — send server-side events</option>
            <option value="off">Off</option>
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" data-testid="tiktok-events-save" onClick={save}>Save TikTok settings</button>
        <button className="btn-ghost" data-testid="tiktok-events-test" onClick={test}>Send test event</button>
      </div>
      {msg ? <p className="text-xs text-muted" data-testid="tiktok-events-msg">{msg}</p> : null}
    </Panel>
  );
};
